// src/pages/Settings.tsx
import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, DollarSign, Save, Lock, ShieldCheck } from 'lucide-react';
import { companyApi, subscriptionsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { SubscriptionsSection } from '../components/SubscriptionsSection';
import  UsageLimits  from './UsageLimits';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    currency: 'MT',
    taxRate: 16,
    taxRegime: 'normal',
    simplificadoRate: 0,
    paymentTerms: 'Net 30',
    invoiceNumberPrefix: 'INV-',
    quotationNumberPrefix: 'QUO-',
    worksheetNumberPrefix: 'WS-',
    purchaseOrderNumberPrefix: 'PO-',
    nextInvoiceNumber: 1,
    nextQuotationNumber: 1,
    nextWorksheetNumber: 1,
    nextPurchaseOrderNumber: 1,
    menuVisibility: {} as Record<string, string[]>,
  });

  // Password secreta de gestão
  const [mgmtPassword, setMgmtPassword] = useState('');
  const [mgmtPasswordConfirm, setMgmtPasswordConfirm] = useState('');
  const [mgmtPasswordSet, setMgmtPasswordSet] = useState(false);
  const [mgmtPasswordLoading, setMgmtPasswordLoading] = useState(false);

  const roleName = typeof user?.role === 'string' ? user.role : user?.role?.roleName;
  const canManagePassword = ['superadmin', 'admin', 'owner', 'manager'].includes(roleName);

  useEffect(() => {
    companyApi.getManagementPasswordStatus()
      .then(res => setMgmtPasswordSet(Boolean(res?.set)))
      .catch(() => setMgmtPasswordSet(false));
  }, []);

  // Carregar configurações gerais
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const settings = await companyApi.getSettings();

        setFormData({
          currency: settings.currency || 'MT',
          taxRate: settings.taxRate || 16,
          taxRegime: settings.taxRegime || 'normal',
          simplificadoRate: settings.simplificadoRate || 0,
          paymentTerms: settings.paymentTerms || 'Net 30',
          invoiceNumberPrefix: settings.invoiceNumberPrefix || 'INV-',
          quotationNumberPrefix: settings.quotationNumberPrefix || 'QUO-',
          worksheetNumberPrefix: settings.worksheetNumberPrefix || 'WS-',
          purchaseOrderNumberPrefix: settings.purchaseOrderNumberPrefix || 'PO-',
          nextInvoiceNumber: Number(settings.nextInvoiceNumber) || 1,
          nextQuotationNumber: Number(settings.nextQuotationNumber) || 1,
          nextWorksheetNumber: Number(settings.nextWorksheetNumber) || 1,
          nextPurchaseOrderNumber: Number(settings.nextPurchaseOrderNumber) || 1,
          menuVisibility: settings.menuVisibility || {},
        });
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        toast.error('Falha ao carregar configurações gerais');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleGeneralSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      const payload = { ...formData };
      if (payload.menuVisibility instanceof Map) {
        payload.menuVisibility = Object.fromEntries(payload.menuVisibility);
      }

      await companyApi.updateSettings(payload);
      toast.success('Configurações gerais salvas com sucesso!');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleManagementPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mgmtPassword.length < 4) {
      toast.error('A password secreta deve ter pelo menos 4 caracteres');
      return;
    }
    if (mgmtPassword !== mgmtPasswordConfirm) {
      toast.error('As passwords não coincidem');
      return;
    }
    setMgmtPasswordLoading(true);
    try {
      await companyApi.setManagementPassword(mgmtPassword);
      setMgmtPasswordSet(true);
      setMgmtPassword('');
      setMgmtPasswordConfirm('');
      toast.success('Password secreta definida com sucesso!');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao definir a password secreta');
    } finally {
      setMgmtPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-12">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <SettingsIcon className="w-9 h-9 text-indigo-600" />
            Configurações da Empresa
          </h1>
          <p className="text-gray-600 mt-2">Gerencie preferências, finanças e subscrição</p>
        </div>
      </div>

      {/* Configurações Financeiras */}
      <form onSubmit={handleGeneralSubmit} className="bg-white rounded-2xl shadow border border-gray-100 p-8">
        <div className="flex items-center gap-3 mb-8">
          <DollarSign className="w-6 h-6 text-emerald-600" />
          <h2 className="text-2xl font-semibold">Configurações Financeiras</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Moeda Principal</label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="MT">MT - Metical Moçambicano</option>
              <option value="USD">USD - Dólar Americano</option>
              <option value="EUR">EUR - Euro</option>
              <option value="ZAR">ZAR - Rand Sul-Africano</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Regime de IVA</label>
            <select
              value={formData.taxRegime}
              onChange={(e) => setFormData(prev => ({ ...prev, taxRegime: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="normal">Regime Normal (16%)</option>
              <option value="isento">Isento</option>
              <option value="simplificado">Regime Simplificado</option>
            </select>
          </div>

          {formData.taxRegime === 'normal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Taxa de IVA Normal (%)</label>
              <input
                type="number"
                value={formData.taxRate}
                onChange={(e) => setFormData(prev => ({ ...prev, taxRate: Number(e.target.value) || 0 }))}
                min="0"
                max="100"
                step="0.1"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Aplicável às vendas em regime normal. Editável (mudanças legislativas).
              </p>
            </div>
          )}

          {formData.taxRegime === 'isento' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Taxa de IVA</label>
              <input
                type="text"
                value="Isento (0%)"
                disabled
                className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gray-100 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Operações isentas não liquidam IVA. Este valor não é editável.
              </p>
            </div>
          )}

          {formData.taxRegime === 'simplificado' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Taxa de Regime Simplificado (%)</label>
              <input
                type="number"
                value={formData.simplificadoRate}
                onChange={(e) => setFormData(prev => ({ ...prev, simplificadoRate: Number(e.target.value) || 0 }))}
                min="0"
                max="100"
                step="0.1"
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Aplicável às vendas em regime simplificado. Editável (mudanças legislativas).
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Termos de Pagamento</label>
            <input
              type="text"
              value={formData.paymentTerms}
              onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
              placeholder="Net 30"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo Fatura</label>
            <input
              type="text"
              value={formData.invoiceNumberPrefix}
              onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumberPrefix: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo Cotação</label>
            <input
              type="text"
              value={formData.quotationNumberPrefix}
              onChange={(e) => setFormData(prev => ({ ...prev, quotationNumberPrefix: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo Folha de Obra</label>
            <input
              type="text"
              value={formData.worksheetNumberPrefix}
              onChange={(e) => setFormData(prev => ({ ...prev, worksheetNumberPrefix: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo Ordem de Compra</label>
            <input
              type="text"
              value={formData.purchaseOrderNumberPrefix}
              onChange={(e) => setFormData(prev => ({ ...prev, purchaseOrderNumberPrefix: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fatura — Próximo Nº</label>
            <input
              type="number"
              min="1"
              value={formData.nextInvoiceNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, nextInvoiceNumber: Number(e.target.value) || 1 }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cotação — Próximo Nº</label>
            <input
              type="number"
              min="1"
              value={formData.nextQuotationNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, nextQuotationNumber: Number(e.target.value) || 1 }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Folha de Obra — Próximo Nº</label>
            <input
              type="number"
              min="1"
              value={formData.nextWorksheetNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, nextWorksheetNumber: Number(e.target.value) || 1 }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ordem de Compra — Próximo Nº</label>
            <input
              type="number"
              min="1"
              value={formData.nextPurchaseOrderNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, nextPurchaseOrderNumber: Number(e.target.value) || 1 }))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          Use os campos "Próximo Nº" para definir a partir de que número cada documento deve começar
          (ex.: já tem faturas 001–120 e quer continuar na 121).
        </p>

        <div className="flex justify-end mt-10">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-3 px-10 py-4 bg-indigo-600 text-white rounded-2xl font-semibold hover:bg-indigo-700 disabled:opacity-70 transition"
          >
            <Save size={20} />
            {loading ? 'Guardando...' : 'Salvar Configurações Financeiras'}
          </button>
        </div>
      </form>

      {/* Password Secreta de Gestão */}
      {canManagePassword && (
        <form onSubmit={handleManagementPasswordSubmit} className="bg-white rounded-2xl shadow border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-6 h-6 text-red-600" />
            <h2 className="text-2xl font-semibold">Password Secreta de Gestão</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Esta password é exigida para apagar vendas no histórico e fechos de caixa. Defina-a e partilhe apenas com quem deve autorizar eliminações.
          </p>

          {mgmtPasswordSet && (
            <div className="mb-6 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Lock className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">Password secreta já definida. Ao guardar uma nova, a anterior é substituída.</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password Secreta</label>
              <input
                type="password"
                value={mgmtPassword}
                onChange={(e) => setMgmtPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-500"
                placeholder="Mínimo 4 caracteres"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Password</label>
              <input
                type="password"
                value={mgmtPasswordConfirm}
                onChange={(e) => setMgmtPasswordConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-500"
                placeholder="Repita a password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <button
              type="submit"
              disabled={mgmtPasswordLoading}
              className="flex items-center gap-3 px-10 py-4 bg-red-600 text-white rounded-2xl font-semibold hover:bg-red-700 disabled:opacity-70 transition"
            >
              <Lock size={20} />
              {mgmtPasswordLoading ? 'A guardar...' : (mgmtPasswordSet ? 'Atualizar Password Secreta' : 'Definir Password Secreta')}
            </button>
          </div>
        </form>
      )}

      {/* Seção de Subscrição */}
      <SubscriptionsSection />

      {/* Seção de Limites de Uso */}
      <UsageLimits />
    </div>
  );
};

export default Settings;